import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { DropDownCmp } from '@/components/atom/dropdown/dropDown'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { formatDefPayload } from '@/lib/utils'
import { fetchVolumeGrid } from '@/services/apis/api'
import { GraphData } from '@/services/types/api.types'
import { AxiosResponse } from 'axios'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type CellData = {
  value: number
  label: string
  color: 'yellow' | 'blue' | 'pink' | 'transparent'
}

type VolumeGridResponseType = AxiosResponse<{ data: GraphData }>

const getUtcDayIndex = (date: string): number => {
  const weekday = dayjs.utc(date).day()
  return weekday === 0 ? 6 : weekday - 1 // convert to Mon=0 ... Sun=6
}

type DayEntry = { date: string; orderCount: number }

const normalizeWeek = (week: DayEntry[]) => {
  const fixed: (DayEntry | null)[] = Array(7).fill(null)
  week.forEach(entry => {
    const index = getUtcDayIndex(entry.date)
    fixed[index] = entry
  })
  return fixed
}

const colorMap = {
  yellow: 'bg-[#FFF9E5] text-[#B45309]',
  blue: 'bg-[#EFF6FF] text-[#1D4ED8]',
  pink: 'bg-[#FEF2F2] text-[#B91C1C]',
  transparent: 'bg-gray-50 text-gray-500',
}

const borderMap = {
  yellow: 'border-[#FDE68A]',
  blue: 'border-[#BFDBFE]',
  pink: 'border-[#FECACA]',
  transparent: 'border-gray-200',
}

const OrdersDueTable = () => {
  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const essentialIds = useMemo(() => formatDefPayload(defPayload), [defPayload])

  const [heatmapData, setHeatmapData] = useState<CellData[][]>([])
  const [loading, setIsLoading] = useState(false)
  const [value, setValue] = useState('CURRENT_MONTH')

  const getCellColorAndLabel = (
    val: number,
    avg: number,
  ): { color: 'yellow' | 'blue' | 'pink' | 'transparent'; label: string } => {
    if (avg === 0 || val === 0) return { color: 'transparent', label: '' }

    const fifty = avg * 0.5
    const oneFifty = avg * 1.5

    if (val <= fifty) return { color: 'blue', label: 'Low' }
    if (val <= oneFifty) return { color: 'yellow', label: 'Medium' }
    return { color: 'pink', label: 'High' }
  }

  const getVolumeGrid = useCallback(async () => {
    if (!essentialIds.customerId || !essentialIds.siteId) return

    setIsLoading(true)
    try {
      const res = (await fetchVolumeGrid({
        ...essentialIds,
        datetime: value,
      })) as VolumeGridResponseType

      if (res.status === 200) {
        const graphData = res.data.data.graphData

        const allValues: number[] = []
        Object.keys(graphData).forEach(weekKey => {
          graphData[weekKey].forEach(entry => {
            allValues.push(entry.orderCount)
          })
        })
        const total = allValues.reduce((acc, val) => acc + val, 0)
        const count = allValues.length
        const average = count > 0 ? total / count : 0

        const weekKeys = Object.keys(graphData)
        const formattedHeatmap = weekKeys.map(key => {
          const normalized = normalizeWeek(graphData[key])
          return normalized.map(entry => {
            const count = entry?.orderCount || 0
            const { color, label } = getCellColorAndLabel(count, average)
            return { value: count, color, label }
          })
        })
        setHeatmapData(formattedHeatmap)
      } else {
        toast.error('Something Went Wrong')
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to fetch orders due by date')
    } finally {
      setIsLoading(false)
    }
  }, [essentialIds, value])

  useEffect(() => {
    if (defPayload.customerId && defPayload.siteId.length > 0) {
      setTimeout(() => {
        getVolumeGrid()
      }, 0)
    }
  }, [defPayload.customerId, defPayload.siteId.length, getVolumeGrid])

  return (
    <div className='flex w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-4 shadow-sm'>
      {/* Header */}
      <div className='mb-4 flex items-center justify-between'>
        <h3 className='text-kpi-heading-2 font-semibold text-primary-foreground'>
          Order Due By Date
        </h3>
        <div className='flex items-center gap-2'>
          <DropDownCmp
            node={{
              fieldName: 'orderDueDropdown',
              dropdownValue: [
                { label: 'Last Month', value: 'LAST_MONTH' },
                { label: 'Current Month', value: 'CURRENT_MONTH' },
                { label: 'Next Month', value: 'NEXT_MONTH' },
              ],
              value: value,
              childClass:
                'h-8 text-kpi-supporting-1 font-medium bg-tertiary border-none rounded-full px-4',
            }}
            handleChange={(_, val) => setValue(val)}
          />
          <ExternalLink className='h-4 w-4 text-primary' />
        </div>
      </div>

      <div className='w-full overflow-x-auto'>
        {loading ? (
          <div className='flex flex-col gap-3 w-full'>
            <table className='w-full min-w-[800px] table-fixed border-separate border-spacing-y-2 border-spacing-x-2'>
              {/* Header Skeleton */}
              <thead>
                <tr>
                  {[
                    'Week',
                    'Mon',
                    'Tue',
                    'Wed',
                    'Thu',
                    'Fri',
                    'Sat',
                    'Sun',
                  ].map(item => (
                    <th
                      key={item}
                      className='rounded-radius-md bg-[#f4f8fc] px-3 py-2'
                    >
                      <div className='flex justify-center'>
                        <GradiantSkelton
                          width='w-12'
                          height='h-5'
                          className='!rounded-md'
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body Skeleton */}
              <tbody>
                {[1, 2, 3, 4].map(row => (
                  <tr key={row}>
                    {/* Week Label */}
                    <td className='h-[79px] rounded-lg bg-[#f4f8fc] p-3'>
                      <GradiantSkelton
                        width='w-16'
                        height='h-5'
                        className='!rounded-md'
                      />
                    </td>

                    {/* Days */}
                    {[1, 2, 3, 4, 5, 6, 7].map(cell => (
                      <td key={cell} className='p-0'>
                        <div className='flex h-[79px] flex-col items-center justify-center rounded-lg border border-[#D9E4F2] bg-[#EDF4FD] px-2 py-3'>
                          <GradiantSkelton
                            width='w-10'
                            height='h-7'
                            className='!rounded-md mb-2'
                          />

                          <GradiantSkelton
                            width='w-14'
                            height='h-3'
                            className='!rounded-sm'
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <table className='w-full min-w-[800px] table-fixed border-separate border-spacing-y-2 border-spacing-x-2'>
            <thead>
              <tr>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Week
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Mon
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Tue
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Wed
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Thu
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Fri
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Sat
                </th>
                <th className='w-[176px] px-3 py-2 rounded-radius-md text-center bg-[#f4f8fc] text-kpi-heading font-semibold text-[#0a0a0a]'>
                  Sun
                </th>
              </tr>
            </thead>
            <tbody>
              {heatmapData.map((days, idx) => (
                <tr key={idx}>
                  <td className='bg-[#f4f8fc] h-[79px] rounded-lg p-3 text-left text-kpi-supporting-1 font-semibold text-[#0a0a0a]'>
                    Week {idx + 1}
                  </td>
                  {days.map((day, dayIdx) => (
                    <td key={dayIdx} className='p-0'>
                      <div
                        className={`flex flex-col items-center justify-center rounded-lg border py-3 px-2 h-[79px] ${colorMap[day.color]} ${borderMap[day.color]}`}
                      >
                        <span className='text-primary-button font-bold leading-none mb-1'>
                          {day.value}
                        </span>
                        {day.label && (
                          <span className='text-badge font-medium leading-none'>
                            {day.label}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default OrdersDueTable
